/**
 * Orbe country dropdown enhancements (Maryam's / Impulse)
 * ---------------------------------------------------------------------------
 *  1. "Search country" box above Orbe's #orbeCountryList.
 *  2. Pre-selects the visitor's detected country each time the popup opens
 *     (see "Detected-country pre-select" below).
 * Theme-side only, Orbe app files untouched. Independent of country-currency-sync.js: selecting a
 * result clicks the same element Orbe's own keyboard/click path uses, so
 * country_code / #md-form__select__country[data-value] update exactly as usual.
 *
 * Orbe behaviour this works around (from orbe.min.js):
 *  - Any click inside the modal closes the dropdown unless the target is on
 *    Orbe's whitelist  -> clicks in the search box don't propagate.
 *  - Esc keydown bubbling to the modal closes the whole popup -> Esc in the box
 *    closes only the dropdown.
 *  - Orbe's ArrowUp/Down walks every <li>, hidden ones too -> patched in capture.
 *
 * If Orbe ever enables its own search (option_search_country), this stays out.
 */
(function () {
  'use strict';

  var LIST_ID = 'orbeCountryList';
  var TOGGLE_ID = 'md-form__select__country';
  var WRAP_CLASS = 'ccs-country-search';
  var DEBOUNCE_MS = 100;

  // Extra search terms per ISO code (normalized, lowercase).
  var ALIASES = {
    GB: ['uk', 'britain', 'great britain', 'england', 'scotland', 'wales'],
    US: ['usa', 'america', 'us', 'united states of america'],
    AE: ['uae', 'dubai', 'emirates', 'abu dhabi'],
    SA: ['ksa', 'saudi'],
    NL: ['holland'],
    KR: ['korea', 'south korea']
  };

  function norm(s) {
    s = String(s || '');
    if (s.normalize) s = s.normalize('NFD').replace(/[̀-ͯ]/g, '');
    return s.toLowerCase().replace(/\s+/g, ' ').trim();
  }
  function safe(fn) {
    return function () {
      try { return fn.apply(this, arguments); } catch (e) {
        if (window.console) console.warn('[country-search]', e);
      }
    };
  }
  function orbe() { try { /* global orbito */ return typeof orbito !== 'undefined' ? orbito : null; } catch (e) { return null; } }
  function isDesktop() { return window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches; }

  /* ---------- Search index ---------------------------------------------------- */
  function buildIndex(ul) {
    var items = [];
    var lis = ul.querySelectorAll('li[role="option"]');
    for (var i = 0; i < lis.length; i++) {
      var li = lis[i];
      var name = li.getAttribute('data-name') || li.textContent;
      var iso = (li.getAttribute('data-country') || '').toUpperCase();
      var full = norm(name);
      var parts = full.split('/').map(function (p) { return p.trim(); }).filter(Boolean);
      items.push({
        li: li,
        order: i,
        display: li.style.display,             // Orbe sets display:list-item inline
        searchable: li.style.display !== 'none', // respect countries Orbe itself hides
        iso: iso.toLowerCase(),
        sortKey: full,
        parts: parts,
        words: full.split(/[\s\/(),.-]+/).filter(Boolean),
        aliases: ALIASES[iso] || []
      });
    }
    return items;
  }

  // Lower rank = better. null = no match.
  function rank(item, q) {
    var i;
    if (item.iso === q || item.aliases.indexOf(q) !== -1) return 0;   // "us", "uk", "dubai"
    for (i = 0; i < item.parts.length; i++) if (item.parts[i].indexOf(q) === 0) return 1; // name / native name starts with
    for (i = 0; i < item.words.length; i++) if (item.words[i].indexOf(q) === 0) return 2; // any word starts with
    if (item.sortKey.indexOf(q) !== -1) return 3;                        // anywhere in the name
    for (i = 0; i < item.aliases.length; i++) if (item.aliases[i].indexOf(q) !== -1) return 3;
    return null;
  }

  /* ---------- One search box per list ------------------------------------------ */
  function bind(ul) {
    if (ul.__ccsSearch) return ul.__ccsSearch;
    var modal = ul.closest('#md-app-embed__modal') || document;
    if (modal.querySelector('.md-form__select__search__input')) return null; // Orbe's native search is on

    // Remove stale boxes left by an Orbe re-render, then inject once.
    var stale = modal.querySelectorAll('.' + WRAP_CLASS);
    for (var s = 0; s < stale.length; s++) {
      if (stale[s].nextElementSibling !== ul) stale[s].parentNode.removeChild(stale[s]);
    }
    var wrap = ul.previousElementSibling && ul.previousElementSibling.classList.contains(WRAP_CLASS)
      ? ul.previousElementSibling : null;
    if (!wrap) {
      wrap = document.createElement('div');
      wrap.className = WRAP_CLASS;
      wrap.hidden = true;
      wrap.innerHTML =
        '<svg class="' + WRAP_CLASS + '__icon" aria-hidden="true" focusable="false" viewBox="0 0 20 20"><circle cx="8.5" cy="8.5" r="5.5"/><path d="M13 13l4.5 4.5"/></svg>' +
        '<input type="search" class="' + WRAP_CLASS + '__input" placeholder="Search country"' +
        ' aria-label="Search country" aria-controls="' + LIST_ID + '" autocomplete="off"' +
        ' autocorrect="off" autocapitalize="off" spellcheck="false" enterkeyhint="go">' +
        '<div class="' + WRAP_CLASS + '__empty" aria-live="polite" hidden>No country found</div>' +
        '<span class="' + WRAP_CLASS + '__status visually-hidden" aria-live="polite"></span>';
      ul.parentNode.insertBefore(wrap, ul);
    }

    var input = wrap.querySelector('input');
    var empty = wrap.querySelector('.' + WRAP_CLASS + '__empty');
    var status = wrap.querySelector('.' + WRAP_CLASS + '__status');
    var toggle = document.getElementById(TOGGLE_ID);
    var items = buildIndex(ul);
    var timer = null;
    var lastQuery = '';

    function visibleItems() {
      return Array.prototype.filter.call(ul.querySelectorAll('li[role="option"]'), function (li) {
        return li.style.display !== 'none';
      });
    }

    function freshIndex() {
      // Orbe may rebuild the <li>s; re-index if the set changed.
      var lis = ul.querySelectorAll('li[role="option"]');
      if (lis.length !== items.length || (items[0] && !ul.contains(items[0].li))) items = buildIndex(ul);
    }

    function applyFilter() {
      clearTimeout(timer); timer = null;
      var q = norm(input.value);
      if (q === lastQuery) return;
      lastQuery = q;

      if (!q) {
        // Restore Orbe's original order + display.
        items.slice().sort(function (a, b) { return a.order - b.order; }).forEach(function (it) {
          it.li.style.display = it.display;
          ul.appendChild(it.li);
        });
        empty.hidden = true;
        status.textContent = '';
        ul.classList.remove('ccs-search-empty');
        ul.scrollTop = 0;
        return;
      }

      var matches = [];
      items.forEach(function (it) {
        var r = it.searchable ? rank(it, q) : null;
        if (r === null) { it.li.style.display = 'none'; return; }
        it.li.style.display = it.display || 'list-item';
        matches.push({ it: it, r: r });
      });
      // Best rank first, alphabetical within each rank.
      matches.sort(function (a, b) {
        return a.r - b.r || (a.it.sortKey < b.it.sortKey ? -1 : a.it.sortKey > b.it.sortKey ? 1 : 0);
      });
      matches.forEach(function (m) { ul.appendChild(m.it.li); });

      var n = matches.length;
      empty.hidden = n > 0;
      ul.classList.toggle('ccs-search-empty', n === 0);
      status.textContent = n ? n + (n === 1 ? ' country found' : ' countries found') : '';
      ul.scrollTop = 0;
    }

    function reset() {
      input.value = '';
      lastQuery = null; // force restore
      applyFilter();
      lastQuery = '';
    }

    function focusOption(li) {
      if (!li) return;
      li.focus();
      var o = orbe();
      if (o && typeof o._setActiveDescendant === 'function' && li.id) {
        try { o._setActiveDescendant(li.id); } catch (e) { /* cosmetic only */ }
      }
    }

    function closeDropdown() {
      var o = orbe();
      if (o && typeof o._hidePanel === 'function' && toggle) {
        o._hidePanel(toggle, ul); // Orbe's own close (also refocuses the toggle)
      } else {
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
        ul.setAttribute('hidden', '');
        if (toggle) toggle.focus();
      }
    }

    function position() {
      // Sit exactly where Orbe positions the list; push the list down below the box.
      var cs = getComputedStyle(ul);
      ul.style.marginTop = '';
      wrap.style.top = cs.top;
      wrap.style.left = cs.left;
      wrap.style.right = cs.right;
      wrap.style.width = ul.offsetWidth ? ul.offsetWidth + 'px' : '';
      wrap.style.zIndex = cs.zIndex;
      wrap.style.backgroundColor = cs.backgroundColor;
      wrap.style.borderTop = cs.borderTopWidth + ' ' + cs.borderTopStyle + ' ' + cs.borderTopColor;
      wrap.style.borderLeft = cs.borderLeftWidth + ' ' + cs.borderLeftStyle + ' ' + cs.borderLeftColor;
      wrap.style.borderRight = cs.borderRightWidth + ' ' + cs.borderRightStyle + ' ' + cs.borderRightColor;
      wrap.style.borderTopLeftRadius = cs.borderTopLeftRadius;
      wrap.style.borderTopRightRadius = cs.borderTopRightRadius;
      input.style.borderColor = cs.borderTopColor;
      input.style.borderRadius = toggle ? getComputedStyle(toggle).borderTopLeftRadius : '';
      ul.style.marginTop = wrap.offsetHeight + 'px';
    }

    var onOpenChange = safe(function () {
      var open = !ul.hasAttribute('hidden');
      if (open === !wrap.hidden) return;
      if (open) {
        freshIndex();
        wrap.hidden = false;
        position();
        // Desktop only: Orbe focuses the first country in a setTimeout(0); go after it.
        if (isDesktop()) setTimeout(function () { try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); } }, 30);
      } else {
        wrap.hidden = true;
        ul.style.marginTop = '';
        reset();
      }
    });

    new MutationObserver(onOpenChange).observe(ul, { attributes: true, attributeFilter: ['hidden'] });

    // Clicks in the box must not reach Orbe's "click anywhere closes the dropdown" handler.
    wrap.addEventListener('click', function (e) { e.stopPropagation(); });

    input.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(safe(applyFilter), DEBOUNCE_MS);
    });

    input.addEventListener('keydown', safe(function (e) {
      // Keep Orbe's modal-level handlers (focus trap, Esc-closes-popup) out of it.
      e.stopPropagation();
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        closeDropdown();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        applyFilter();
        focusOption(visibleItems()[0]);
      } else if (e.key === 'Enter') {
        e.preventDefault(); // never submit the form from here
        applyFilter();
        var first = visibleItems()[0];
        // Same path as Orbe's own Enter on an option: click the option's link span.
        if (first) (first.firstElementChild || first).click();
      }
    }));
    input.addEventListener('keyup', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') e.stopPropagation();
    });

    // Arrow keys on the list: skip hidden items (Orbe's handler doesn't), ArrowUp from
    // the first item returns to the search box. Capture phase -> runs before Orbe's.
    ul.addEventListener('keydown', safe(function (e) {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      var vis = visibleItems();
      var idx = vis.indexOf(document.activeElement);
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'ArrowDown') focusOption(vis[Math.min(idx + 1, vis.length - 1)]);
      else if (idx <= 0) input.focus();
      else focusOption(vis[idx - 1]);
    }), true);

    ul.__ccsSearch = { reset: reset };
    onOpenChange();
    return ul.__ccsSearch;
  }

  function ensure() {
    var ul = document.getElementById(LIST_ID);
    if (ul && ul.parentNode) bind(ul);
  }

  /* ---------- Detected-country pre-select ----------------------------------------
     Orbe pre-selects the visitor's saved market (e.g. UAE) while its title shows the
     detected location. Every time the popup opens (Orbe auto-open or our header
     button) select the *detected* country instead, through the same click path a
     shopper uses. Nothing is submitted, so market and currency only change after
     "Shop now". If detection fails or the country isn't in the list, Orbe's default
     selection is left as is. */
  var DETECTED_KEY = 'ccs_detected_country';
  var MODAL_ID = 'md-app-embed__modal';
  var detectRequest = null;

  function validCode(c) { return typeof c === 'string' && /^[A-Za-z]{2}$/.test(c) ? c.toUpperCase() : null; }
  function ssGet(k) { try { return sessionStorage.getItem(k); } catch (e) { return null; } }
  function ssSet(k, v) { try { sessionStorage.setItem(k, v); } catch (e) { /* private mode */ } }

  // Synchronous sources: Orbe's live state, Orbe's own session cache, our cache.
  function detectedNow() {
    var o = orbe();
    var code = o && validCode(o.remoteCountryCode);
    if (!code) {
      try { code = validCode(JSON.parse(ssGet('orbeSession')).geolocation.countryCode); } catch (e) { code = null; }
    }
    if (code) { ssSet(DETECTED_KEY, code); return code; }
    return validCode(ssGet(DETECTED_KEY));
  }

  // Fallback: Shopify's own geolocation suggestion. Cached for the session on success;
  // at most one attempt per page on failure. XHR rather than fetch: other apps wrap
  // window.fetch and re-throw network failures as console errors.
  function detectCountry() {
    var code = detectedNow();
    if (code) return Promise.resolve(code);
    if (!detectRequest) {
      detectRequest = new Promise(function (resolve) {
        try {
          var xhr = new XMLHttpRequest();
          xhr.open('GET', '/browsing_context_suggestions.json?country[enabled]=true');
          xhr.timeout = 5000;
          xhr.onload = function () {
            var c = null;
            try {
              var d = JSON.parse(xhr.responseText);
              c = validCode(d && d.detected_values && d.detected_values.country && d.detected_values.country.handle);
            } catch (e) { /* bad JSON */ }
            if (c) ssSet(DETECTED_KEY, c);
            resolve(c);
          };
          xhr.onerror = xhr.ontimeout = xhr.onabort = function () { resolve(null); };
          xhr.send();
        } catch (e) { resolve(null); }
      });
    }
    return detectRequest;
  }

  function bindPreselect() {
    var modal = document.getElementById(MODAL_ID);
    if (!modal || modal.__ccsPreselect) return;
    modal.__ccsPreselect = true;

    var userPicked = false;  // shopper chose something during this open -> never override
    var wasOpen = false;

    function isOpen() { return !modal.classList.contains('orbe--hidden'); }

    var syncSelected = safe(function () {
      // Mirror Orbe's selection (aria-current on the link) as aria-selected on the options.
      var toggle = document.getElementById(TOGGLE_ID);
      var ul = document.getElementById(LIST_ID);
      if (!toggle || !ul) return;
      var value = toggle.getAttribute('data-value');
      var lis = ul.querySelectorAll('li[role="option"]');
      for (var i = 0; i < lis.length; i++) {
        lis[i].setAttribute('aria-selected', lis[i].getAttribute('data-country') === value ? 'true' : 'false');
      }
    });

    var apply = safe(function (code) {
      if (!code || userPicked || !isOpen()) return;
      var toggle = document.getElementById(TOGGLE_ID);
      var ul = document.getElementById(LIST_ID);
      if (!toggle || !ul) return;
      var li = ul.querySelector('li[data-country="' + code + '"]');
      var link = li && li.querySelector('.md-form__select__country__list-link');
      if (!link || li.style.display === 'none') return;          // not offered: keep Orbe's default
      if (toggle.getAttribute('data-value') !== code) link.click(); // Orbe's own select handler
      var hiddenInput = document.getElementById('orbe-country-selector-popup-id');
      if (hiddenInput) hiddenInput.value = code;
      syncSelected();
    });

    var onModalChange = safe(function () {
      var open = isOpen();
      if (open && !wasOpen) {
        userPicked = false;
        var now = detectedNow();
        if (now) apply(now);
        else detectCountry().then(apply);
      }
      wasOpen = open;
    });

    new MutationObserver(onModalChange).observe(modal, { attributes: true, attributeFilter: ['class'] });

    // A real click / Enter on a country during this open wins over a late detection result.
    modal.addEventListener('click', function (e) {
      if (e.isTrusted && e.target && e.target.closest && e.target.closest('#' + LIST_ID)) userPicked = true;
    }, true);
    modal.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target && e.target.closest &&
          (e.target.closest('#' + LIST_ID) || e.target.closest('.' + WRAP_CLASS))) userPicked = true;
    }, true);

    var toggleEl = document.getElementById(TOGGLE_ID);
    if (toggleEl) new MutationObserver(syncSelected).observe(toggleEl, { attributes: true, attributeFilter: ['data-value'] });
    syncSelected();
    onModalChange();

    // Warm the cache in idle time so the first open doesn't wait on the network.
    var idle = window.requestIdleCallback || function (fn) { return setTimeout(fn, 2000); };
    idle(function () { if (!detectedNow()) detectCountry(); });
  }

  /* ---------- Init --------------------------------------------------------------- */
  var init = safe(function () {
    ensure();
    safe(bindPreselect)();
    // If Orbe re-renders the modal, the new list gets bound on the next toggle click
    // (capture: runs before Orbe's handler flips the list's hidden attribute).
    document.addEventListener('click', safe(function (e) {
      if (e.target && e.target.closest && e.target.closest('#' + TOGGLE_ID)) ensure();
    }), true);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
